import React, { useRef } from "react";

import EnterpriseKpisPanel from "../../../../../intelligence-briefing-library/EnterpriseKpisPanel";
import type { HawkeyeMetricsAccountsInput } from "../../../../../intelligence-briefing-library/hooks/useHawkeyeMetrics";
import {
  isAccountTeamRole,
  isRole,
  ROLES,
} from "../../../../../Utils/Constants/RoleConstants";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../../../../Utils/Helper/LocalStorage";
import TempPublicDataPanel from "../../../../TempViewsRendering/views/TempPublicDataPanel";
import BirdsEyeOverview, {
  type BirdsEyeClusterSummary,
} from "../BirdsEyeOverview";
import BriefingWorkspace, {
  type BriefingParentTab,
  type BriefingWorkspaceTab,
} from "../BriefingWorkspace";
import type { CreateWatchlistModalSuccess } from "../CreateWatchlistModal";
import KpiDataAvailableToggle from "../KpiDataAvailableToggle";
import PortfolioSponsorOnlyState from "../PortfolioSponsorOnlyState";

interface PortfolioTabProps {
  accountsList: any[];
  searchQuery: string;
  selectedCustomerIds: string[];
  highlightedAccountIds?: string[];
  onHighlightedAccountIdsChange?: (accountIds: string[]) => void;
  onAccountSelection: (account: any) => void;
  onTabChange?: (tabId: string) => void;
  briefingParentTab?: BriefingParentTab;
  onBriefingParentTabChange?: (tab: BriefingParentTab) => void;
  briefingSubTab?: BriefingWorkspaceTab;
  onBriefingSubTabChange?: (tab: BriefingWorkspaceTab) => void;
  onBirdsEyeClusterGroupsChange?: (groups: BirdsEyeClusterSummary[]) => void;
  onSelectedBirdsEyeAccountIdsChange?: (accountIds: string[]) => void;
  selectedBirdsEyeAccountIds?: string[];
  onSaveAsWatchlist?: (accountIds: string[]) => void;
  onSaveAccountsSuccess?: (result: CreateWatchlistModalSuccess) => void;
  showUnavailableKpiData?: boolean;
  onShowUnavailableKpiDataChange?: (show: boolean) => void;
  kpiDataCounts?: {
    available: number;
    unavailable: number;
    loading?: boolean;
  };
  onKpiDataCountsChange?: (counts: {
    available: number;
    unavailable: number;
    loading: boolean;
  }) => void;
  onAccountsWithoutKpiDataChange?: (accountIds: string[]) => void;
  showUnavailableBriefingData?: boolean;
  onShowUnavailableBriefingDataChange?: (show: boolean) => void;
  briefingDataCounts?: {
    available: number;
    unavailable: number;
    loading?: boolean;
  };
  onBriefingDataCountsChange?: (counts: {
    available: number;
    unavailable: number;
    loading: boolean;
  }) => void;
  onAccountsWithoutBriefingDataChange?: (accountIds: string[]) => void;
}

const PortfolioTab: React.FC<PortfolioTabProps> = ({
  accountsList,
  selectedCustomerIds,
  highlightedAccountIds = [],
  onHighlightedAccountIdsChange,
  onTabChange,
  briefingParentTab,
  onBriefingParentTabChange,
  briefingSubTab,
  onBriefingSubTabChange,
  onBirdsEyeClusterGroupsChange,
  onSelectedBirdsEyeAccountIdsChange,
  selectedBirdsEyeAccountIds = [],
  onSaveAsWatchlist,
  onSaveAccountsSuccess,
  showUnavailableKpiData = true,
  onShowUnavailableKpiDataChange,
  kpiDataCounts = { available: 0, unavailable: 0, loading: true },
  onKpiDataCountsChange,
  onAccountsWithoutKpiDataChange,
  showUnavailableBriefingData = true,
  onShowUnavailableBriefingDataChange,
  briefingDataCounts = { available: 0, unavailable: 0, loading: true },
  onBriefingDataCountsChange,
  onAccountsWithoutBriefingDataChange,
}: PortfolioTabProps) => {
  // Get user role from localStorage
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userRole = loggedInUser?.role;

  const isExecutiveSponsor = isRole(userRole, ROLES.EXECUTIVE_SPONSOR);
  const isAccountTeamUser = isAccountTeamRole(userRole);
  const canAccessPortfolio = isExecutiveSponsor || isAccountTeamUser;

  // Handle switch to Any tab
  const handleSwitchToAny = () => {
    if (onTabChange) {
      onTabChange("any");
    }
  };

  const hawkeyeAccounts = React.useMemo(
    () =>
      (accountsList || [])
        .filter((account: any) =>
          selectedCustomerIds.includes(String(account.customer_id))
        )
        .map((account: any) => ({
          id: String(account.customer_id || account.id || ""),
          name: String(
            account.name || account.account_name || "Unknown Account"
          ),
          city: account.city,
          country: account.country,
          region: account.region,
          latitude: account.latitude,
          longitude: account.longitude,
          primary_sponsors: account.primary_sponsors ?? null,
          primary_sponsors_id: account.primary_sponsors_id ?? null,
          secondary_sponsors: account.secondary_sponsors ?? null,
          secondary_sponsors_id: account.secondary_sponsors_id ?? null,
        })),
    [accountsList, selectedCustomerIds]
  );

  /** Same scope as hawkeyeAccounts: only accounts selected for My Portfolio. */
  const birdsEyeAccounts = React.useMemo(
    () =>
      (accountsList || [])
        .filter((account: any) =>
          selectedCustomerIds.includes(String(account.customer_id))
        )
        .map((account: any) => ({
          id: String(account.customer_id || account.id || ""),
          name: String(
            account.name || account.account_name || "Unknown Account"
          ),
          city: account.city,
          country: account.country,
          region: account.region,
          latitude: account.latitude,
          longitude: account.longitude,
        })),
    [accountsList, selectedCustomerIds]
  );

  const metricsAccountsInput = React.useMemo<HawkeyeMetricsAccountsInput>(
    () => ({
      selectedAccounts: birdsEyeAccounts,
      allWatchlistAccounts: birdsEyeAccounts,
      watchlistId: null,
      selectedMeasurementDate: null,
      latestMeasurementDate: null,
    }),
    [birdsEyeAccounts]
  );

  const briefingPanelRef = useRef<HTMLDivElement>(null);

  // Show message for users without portfolio access
  if (!canAccessPortfolio) {
    return (
      <div className="h-full w-full min-w-0 flex-1">
        <div className="main-content flex min-h-[calc(100vh-15.5rem)] items-stretch overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <PortfolioSponsorOnlyState
            variant="panel"
            onSwitchToAny={handleSwitchToAny}
            className="w-full"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex-1 min-w-0 h-full">
      <div
        ref={briefingPanelRef}
        className="main-content flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
      >
        <div className="shrink-0 px-3 pt-3 pb-1">
          <h1 className="section-title mb-0">Insights</h1>
        </div>
        <div className="min-h-0 flex-1">
          <BriefingWorkspace
            briefingParentTab={briefingParentTab}
            onBriefingParentTabChange={onBriefingParentTabChange}
            briefingSubTab={briefingSubTab}
            onBriefingSubTabChange={onBriefingSubTabChange}
            headerRight={
              <KpiDataAvailableToggle
                variant="briefing"
                checked={showUnavailableBriefingData}
                onCheckedChange={checked =>
                  onShowUnavailableBriefingDataChange?.(checked)
                }
                unavailableCount={briefingDataCounts.unavailable}
                loading={briefingDataCounts.loading !== false}
              />
            }
            kpiHeaderRight={
              <KpiDataAvailableToggle
                checked={showUnavailableKpiData}
                onCheckedChange={checked =>
                  onShowUnavailableKpiDataChange?.(checked)
                }
                availableCount={kpiDataCounts.available}
                unavailableCount={kpiDataCounts.unavailable}
                loading={kpiDataCounts.loading !== false}
              />
            }
            tempPublicDataContent={
              <TempPublicDataPanel
                accounts={hawkeyeAccounts.map(a => ({
                  id: a.id,
                  name: a.name,
                }))}
                highlightedAccountIds={highlightedAccountIds}
                onHighlightedAccountIdsChange={onHighlightedAccountIdsChange}
                onSaveAsWatchlist={onSaveAsWatchlist}
                selectionPanelRef={briefingPanelRef}
                showUnavailableBriefingData={showUnavailableBriefingData}
                onBriefingDataCountsChange={onBriefingDataCountsChange}
                onAccountsWithoutBriefingDataChange={
                  onAccountsWithoutBriefingDataChange
                }
              />
            }
            businessKpiContent={
              <EnterpriseKpisPanel
                accounts={hawkeyeAccounts}
                highlightedAccountIds={highlightedAccountIds}
                onHighlightedAccountIdsChange={onHighlightedAccountIdsChange}
                onSaveAsWatchlist={onSaveAsWatchlist}
                showUnavailableKpiData={showUnavailableKpiData}
                onKpiDataCountsChange={onKpiDataCountsChange}
                onAccountsWithoutKpiDataChange={onAccountsWithoutKpiDataChange}
                selectionPanelRef={briefingPanelRef}
              />
            }
            birdsEyeContent={
              <BirdsEyeOverview
                metricsAccountsInput={metricsAccountsInput}
                onClusterGroupsChange={onBirdsEyeClusterGroupsChange}
                onSelectedAccountIdsChange={onSelectedBirdsEyeAccountIdsChange}
                selectedAccountIds={selectedBirdsEyeAccountIds}
                onSaveAsWatchlist={onSaveAsWatchlist}
                onSaveAccountsSuccess={onSaveAccountsSuccess}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default PortfolioTab;
